import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useLang } from "@/contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", plan: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const onPlan = (e) => {
      setForm((f) => ({ ...f, plan: e.detail, subject: `Plan: ${e.detail} TIPS` }));
    };
    window.addEventListener("hakan-plan-select", onPlan);
    return () => window.removeEventListener("hakan-plan-select", onPlan);
  }, []);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t.contact.error);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contact.success);
      setForm({ name: "", email: "", subject: "", message: "", plan: "" });
    } catch (err) {
      console.error(err);
      toast.error(t.contact.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-20 md:py-32 border-t border-white/10 bg-black"
    >
      <div className="container-hk">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <div className="text-[10px] md:text-xs tracking-[0.35em] text-[var(--hk-red)] font-bold mb-3">
              // 007 · {t.contact.overline}
            </div>
            <h2 className="font-display text-4xl md:text-6xl uppercase leading-[0.95] tracking-tight mb-6">
              {t.contact.title}
            </h2>
            <p className="text-sm md:text-base text-white/60 leading-relaxed max-w-md">
              {t.contact.subtitle}
            </p>

            <div className="mt-10 space-y-4 text-sm text-white/70">
              <div className="flex items-center gap-3">
                <span className="w-1.5 h-1.5 bg-[var(--hk-red)]" />
                Istanbul, Türkiye
              </div>
              <div className="flex items-center gap-3">
                <span className="w-1.5 h-1.5 bg-[var(--hk-red)]" />
                doubles@hakan-tips.com
              </div>
              <div className="flex items-center gap-3">
                <span className="w-1.5 h-1.5 bg-[var(--hk-red)]" />
                Mon — Sun · 09:00 – 23:00 GMT+3
              </div>
            </div>
          </div>

          <motion.form
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            onSubmit={submit}
            data-testid="contact-form"
            className="lg:col-span-7 space-y-4"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                name="name"
                value={form.name}
                onChange={onChange}
                placeholder={t.contact.name}
                required
                testid="contact-name"
              />
              <Input
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                placeholder={t.contact.email}
                required
                testid="contact-email"
              />
            </div>
            <Input
              name="subject"
              value={form.subject}
              onChange={onChange}
              placeholder={t.contact.subject}
              testid="contact-subject"
            />
            <textarea
              name="message"
              value={form.message}
              onChange={onChange}
              placeholder={t.contact.message}
              rows={6}
              required
              data-testid="contact-message"
              className="w-full bg-transparent border border-white/15 px-5 py-4 text-sm text-white placeholder-white/40 focus:border-[var(--hk-red)] transition-colors"
            />
            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit"
              className="btn-red px-8 py-4 text-xs font-bold tracking-[0.3em] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? t.contact.sending : `${t.contact.submit} →`}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

const Input = ({ testid, ...props }) => (
  <input
    {...props}
    data-testid={testid}
    className="w-full bg-transparent border border-white/15 px-5 py-4 text-sm text-white placeholder-white/40 focus:border-[var(--hk-red)] transition-colors"
  />
);

export default ContactSection;
